import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

/**
 * GradientNode
 * ------------
 * Generates linear, radial, or conic gradients.
 * Outputs a canvas — useful as a color source for blending or masking.
 */
export class GradientNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Gradient";

    this.properties = {
      type: "linear",
      color1: "#ff0000",
      color2: "#0000ff",
      color3: "#00ff00",
      angle: 0,
      threeColor: false,
      canvasSize: 1500,
    };

    this.addOutput("Canvas", "canvas");

    this.addWidget("combo", "Type", "linear", (val) => {
      this.properties.type = val;
    }, { values: ["linear", "radial", "conic"] });

    this.addWidget("toggle", "3 Colors", false, (val) => {
      this.properties.threeColor = val;
    });

    addDrivableSlider(this, "Angle", "angle", 0, 0, 360, 1);

    finalizeDrivableSliders(this, 0);

    this._outCanvas = document.createElement("canvas");
    this._outCanvas.width = this.properties.canvasSize;
    this._outCanvas.height = this.properties.canvasSize;
    this._outCtx = this._outCanvas.getContext("2d");

    // Color pickers drawn inline
    this._colorWidgets = [];
    this._colorWidgets.push(this.addWidget("color", "Color 1", this.properties.color1, (val) => {
      this.properties.color1 = val;
    }));
    this._colorWidgets.push(this.addWidget("color", "Color 2", this.properties.color2, (val) => {
      this.properties.color2 = val;
    }));
    this._colorWidgets.push(this.addWidget("color", "Color 3", this.properties.color3, (val) => {
      this.properties.color3 = val;
    }));

    this.size = [200, 170];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onExecute() {
    syncDrivableSliders(this);

    const { type, color1, color2, color3, angle, threeColor, canvasSize } = this.properties;
    const ctx = this._outCtx;
    const w = canvasSize;
    const h = canvasSize;
    const cx = w / 2;
    const cy = h / 2;

    ctx.clearRect(0, 0, w, h);

    let gradient;

    if (type === "linear") {
      const rad = (angle * Math.PI) / 180;
      const len = Math.max(w, h);
      const x1 = cx - Math.cos(rad) * len / 2;
      const y1 = cy - Math.sin(rad) * len / 2;
      const x2 = cx + Math.cos(rad) * len / 2;
      const y2 = cy + Math.sin(rad) * len / 2;
      gradient = ctx.createLinearGradient(x1, y1, x2, y2);
    } else if (type === "radial") {
      gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, w / 2);
    } else {
      gradient = ctx.createConicGradient((angle * Math.PI) / 180, cx, cy);
    }

    gradient.addColorStop(0, color1);
    if (threeColor) {
      gradient.addColorStop(0.5, color3);
    }
    gradient.addColorStop(1, color2);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    this.setOutputData(0, this._outCanvas);
  }
}

LiteGraph.registerNodeType("generators/Gradient", GradientNode);
